import React, { useMemo, useState } from 'react'
import classNames from 'classnames'
import astro from '../common/lib/astro'
import { HighchartsHourlyGraph } from '../features/common/'
import { DayForecast, ForecastCurrentConditions } from './'
import { getAstroValue, getUserUnitI, timeFormatForUser, isSomething } from '../common/ambient'
import { getDailyForecastLimit } from '../features/payment'
import { FormattedDataPoint } from '../features/device/'
import PropTypes from 'prop-types'

const WIND_GUST_HIGH_MPH = 18

export default function ForecastLongTermV2 ({ forecast, user, onClose, label }) {
  // Selected day index (default to today)
  const [selectedDayIndex, setSelectedDayIndex] = useState(0)
  // Graph view toggle (default to temperature)
  const [graphView, setGraphView] = useState('temperature')

  // Daily forecasts - filtered and limited
  const dailyForecasts = useMemo(() => {
    if (!forecast?.daily?.data) return []
    const days = forecast.daily.data.filter(f =>
      moment(f.time * 1000).startOf('day').valueOf() >=
      moment().startOf('day').valueOf()
    )
    return days?.slice(0, getDailyForecastLimit(user)) || []
  }, [forecast, user])

  // Filter hourly data for the selected day
  const hourlyDataForSelectedDay = useMemo(() => {
    if (!forecast?.hourly?.data || !dailyForecasts[selectedDayIndex]) return []

    // For the first day (today), show the next 24 hours
    if (selectedDayIndex === 0) {
      return forecast.hourly.data.slice(0, 24)
    }

    // For other days, filter by dayOfYear
    const selectedDay = dailyForecasts[selectedDayIndex]
    const selectedDayOfYear = moment(selectedDay.time * 1000).dayOfYear()

    return forecast.hourly.data.filter(h =>
      moment(h.time * 1000).dayOfYear() === selectedDayOfYear
    )
  }, [forecast, dailyForecasts, selectedDayIndex])

  // Transform hourly data for temperature graph
  const temperatureGraphData = useMemo(() => {
    return hourlyDataForSelectedDay.map(hour => ({
      value: Math.round(hour.temperature),
      time: hour.time,
      label: moment(hour.time * 1000).format(
        getUserUnitI('hour24', user) === 1 ? 'H' : 'ha'
      ),
      icon: hour.icon
    }))
  }, [hourlyDataForSelectedDay, user])

  // Transform hourly data for precipitation graph
  const precipitationGraphData = useMemo(() => {
    return hourlyDataForSelectedDay.map(hour => ({
      value: hour.precipIntensity || 0,
      time: hour.time,
      label: moment(hour.time * 1000).format(
        getUserUnitI('hour24', user) === 1 ? 'H' : 'ha'
      ),
      icon: hour.icon
    }))
  }, [hourlyDataForSelectedDay, user])

  // Selected day date label
  const selectedDayLabel = useMemo(() => {
    if (!dailyForecasts[selectedDayIndex]) return ''
    const selectedDay = dailyForecasts[selectedDayIndex]
    const date = moment(selectedDay.time * 1000)
    if (selectedDayIndex === 0) {
      return `Today, ${date.format('MMMM D')}`
    }
    return date.format('dddd, MMMM D')
  }, [dailyForecasts, selectedDayIndex])

  // Check if selected day is today
  const isSelectedDayToday = selectedDayIndex === 0

  // Get forecast data for the selected day
  const selectedDayForecastData = useMemo(() => {
    if (!dailyForecasts[selectedDayIndex]) return null

    // Use current conditions if it's today
    if (isSelectedDayToday && forecast.currently) {
      return forecast.currently
    }

    // Otherwise use daily forecast
    return dailyForecasts[selectedDayIndex]
  }, [dailyForecasts, selectedDayIndex, isSelectedDayToday, forecast])

  // Sunrise/sunset times for selected day
  const sunTimes = useMemo(() => {
    if (!forecast || !dailyForecasts[selectedDayIndex]) return { sunrise: '--', sunset: '--' }
    const getValue = getAstroValue(moment, {
      lat: forecast.lat,
      lon: forecast.lon,
      tz: forecast.tz
    })

    const selectedDay = dailyForecasts[selectedDayIndex]
    const selectedDayTime = moment(selectedDay.time * 1000).valueOf()

    return {
      sunrise: getValue(astro.solar.rise, selectedDayTime).format(timeFormatForUser(user)),
      sunset: getValue(astro.solar.set, selectedDayTime).format(timeFormatForUser(user))
    }
  }, [forecast, dailyForecasts, selectedDayIndex, user])

  // Helper to determine if hour is in next day (for alt styling)
  const getAlt = (time) => {
    if (!forecast?.hourly?.data?.[0]) return
    const todayTime = moment(forecast.hourly.data[0].time * 1000).startOf('day')
    const thisTime = moment(time * 1000)
    if (thisTime.dayOfYear() === todayTime.dayOfYear()) {
      return
    }
    if (Math.floor(moment.duration(thisTime.diff(todayTime)).asDays()) > 1) {
      return
    }
    return 'alt'
  }

  if (!forecast) {
    return null
  }

  const { currently = {}, daily = {} } = forecast

  return (
    <div className='component-forecast-long-term-v2' onClick={e => e.stopPropagation()}>
      {/* <a className='close' onClick={onClose} /> */}

      {/* Header with location */}
      

      {/* Temperature Graph Section with Hourly Data */}
      <div className='temperature-graph-section'>
        {/* Header with date and graph toggle */}
        <div className='hourly-header'>
          <div className='selected-date'><b>{selectedDayLabel}</b>{label ? ': ' + label : ''}</div>
          <div className='graph-toggle'>
            <span className='toggle-label'>Graph view</span>
            <div className='toggle-buttons'>
              <button
                className={classNames('toggle-btn', { 'active': graphView === 'temperature' })}
                onClick={() => setGraphView('temperature')}
              >
                Temperature
              </button>
              <button
                className={classNames('toggle-btn', { 'active': graphView === 'precipitation' })}
                onClick={() => setGraphView('precipitation')}
              >
                Precipitation
              </button>
            </div>
          </div>
        </div>

        {hourlyDataForSelectedDay.length === 0 ? (
          <div className='no-hourly-data'>
            No hourly forecast data available for this day.
          </div>
        ) : (
          <div className='hourly-wrap'>
            <div className={classNames('scrollable-wrap', `graph-view-${graphView}`)} style={{ width: `${hourlyDataForSelectedDay.length * 78}px` }}>

              {/* Temperature or Precipitation Graph */}
              <div className='graph-container'>
                <HighchartsHourlyGraph
                  data={graphView === 'temperature' ? temperatureGraphData : precipitationGraphData}
                  height={180}
                  type={graphView}
                />
              </div>
              {/* Precipitation Row */}
              <div className='precip labels'>
                {hourlyDataForSelectedDay.map((hourData, i) => {
                  return (
                    <div className={classNames('label')} key={`precip-${i}`}>
                      <span className='drop'/><span className='fdp-val'>{parseInt(hourData.precipProbability * 100, 10)}%</span>
                      {/* <FormattedDataPoint type='dailyrainin' value={hourData.precipIntensity} /> */}
                    </div>
                  )
                })}
              </div>

              {/* Temperature Row (shown when viewing precipitation graph) */}
              <div className='temperature labels'>
                {hourlyDataForSelectedDay.map((hourData, i) => {
                  return (
                    <div className={classNames('label')} key={`temp-${i}`}>
                      <div className={classNames('weather-icon', hourData.icon)} />
                      <span className='temp-val'>{Math.round(hourData.temperature)}°</span>
                    </div>
                  )
                })}
              </div>


              {/* Wind Row */}
              <div className='wind labels'>
                {hourlyDataForSelectedDay.map((hourData, i) => {
                  return (
                    <div
                      className={classNames('label', {
                        'high-winds': hourData.windGust && hourData.windGust > WIND_GUST_HIGH_MPH
                      })}
                      key={`wind-${i}`}
                    >
                      <div className='dir-arrow-row'>
                        {hourData.windBearing
                          ? <FormattedDataPoint type='winddir' value={hourData.windBearing} />
                          : <span className='no-wind'>--</span>
                        }
                        <div
                          className={classNames('arrow', { 'no-wind': !isSomething(hourData.windBearing) })}
                          style={isSomething(hourData.windBearing)
                            ? { transform: `rotate(${hourData.windBearing + 180}deg)` }
                            : {}
                          }
                        />
                      </div>
                      <FormattedDataPoint type='windspeedmph' value={hourData.windSpeed} valueTransform={Math.round} />
                      {hourData.windGust && hourData.windGust > WIND_GUST_HIGH_MPH &&
                        <FormattedDataPoint type='windgustmph' value={hourData.windGust} valueTransform={Math.round} />
                      }
                    </div>
                  )
                })}
              </div>

            </div>
          </div>
        )}
      </div>

      <div className='bottom-section'>
        <ForecastCurrentConditions
          data={selectedDayForecastData}
          mode={isSelectedDayToday ? 'current' : 'daily'}
          dailyHigh={dailyForecasts[selectedDayIndex]?.temperatureMax}
          dailyLow={dailyForecasts[selectedDayIndex]?.temperatureMin}
          sunTimes={sunTimes}
          dailySummary={dailyForecasts[selectedDayIndex]?.summary}
        />


        <div className='forecast-grid-wrap'>
          <div className='five-day-forecast'>
            {dailyForecasts.map((f, i) => {
              const dayOfWeek = moment(f.time * 1000).day()
              const isWeekend = dayOfWeek === 0 || dayOfWeek === 6 // Sunday or Saturday
              return (
                <a
                  className='day-forecast-clickable'
                  onClick={() => setSelectedDayIndex(i)}
                  key={`forecast-${f.time}`}
                >
                  {i === selectedDayIndex && <div className='selected-indicator' />}
                  <DayForecast
                    className={classNames({
                      'alt': isWeekend,
                      'selected': i === selectedDayIndex
                    })}
                    title={
                      <>
                        <span className='day'>{moment(f.time * 1000).format('ddd')}</span>
                        <span className='date'>{moment(f.time * 1000).format('M/D')}</span>
                      </>
                    }
                    forecast={{ ...f, int: true }}
                  />
                </a>
              )
            })}
          </div>
          <div className='forecast-instructions'>
            Select a day to see detailed hourly forecast
          </div>
        </div>

      </div>

      <p>
        Weather forecast by <span className='accuweather-logo' />
      </p>
    </div>
  )
}

ForecastLongTermV2.propTypes = {
  forecast: PropTypes.object,
  user: PropTypes.object.isRequired,
  onClose: PropTypes.func,
  label: PropTypes.string
}

ForecastLongTermV2.displayName = 'ForecastLongTermV2'
